/**
 * TestRunSetup — what the person selected for a test run, under the Test Run
 * Details header: location, eSIM, APN, forced network type, forced network,
 * and the modem the platform allocated.
 *
 * Overrides come from the run's configuration snapshot. The eSIM shown is the
 * profile the execution actually used (a group or shared-pool pick resolves to
 * one member at dispatch). Quick-test runs created before the snapshot carried
 * the overrides show "—" for them: what was picked was never stored.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { FORCED_RAT_LABELS, parseSharedPoolSentinel, type ForcedRat, type TestRun } from '@simcheck/shared-types';
import { getGroup, getProfile } from '@/services/esimService';

interface TestRunSetupProps {
  run: TestRun;
  locationName?: string;
}

interface Resolved {
  esimName?: string;
  esimNetwork?: string;
  esimGroup?: string;
  selectedGroup?: string;
  apn?: string;
  modemModel?: string;
  networkName?: string;
}

/** Profile names often end in a short ICCID, e.g. "… (…86554)"; show the full one instead. */
function esimLabel(name: string | undefined, iccid: string | undefined): string | undefined {
  const base = (name || '').replace(/\s*\(…\d+\)\s*$/, '').trim();
  if (base && iccid) return `${base} (${iccid})`;
  return base || iccid || undefined;
}

function formatMccMnc(mccMnc: string): string {
  return `${mccMnc.slice(0, 3)}/${mccMnc.slice(3)}`;
}

export function TestRunSetup({ run, locationName }: TestRunSetupProps) {
  const config: any = run.configuration || {};
  const esimCfg = config.esim;
  const allocation = (run as any).allocation || {};
  const forcedRat: ForcedRat | undefined = config.forcedRat;
  const forcedNetwork: string | undefined = config.forcedNetworkMccMnc;
  const apnOverride: string | undefined = config.inputs?.apn;
  // Quick-test runs used to snapshot `esim: none` and no overrides.
  const legacyQuickRun = !!run.flowId?.startsWith('sys_quick_') && (!esimCfg || esimCfg.mode === 'none');

  const [resolved, setResolved] = useState<Resolved>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next: Resolved = {};

      let profileId: string | undefined;
      if (run.executionId) {
        try {
          const snap = await getDoc(doc(db, 'executions', run.executionId));
          const data: any = snap.data() || {};
          profileId = data.esimProfileId || undefined;
          next.modemModel = data.modemModel || undefined;
          next.apn = data.apn || undefined;
        } catch {
          // No access or not created yet — fall back to the run doc.
        }
      }
      if (!profileId) profileId = (run.result?.outputs as any)?.eSimProfileId;
      if (!profileId && esimCfg?.mode === 'single' && !parseSharedPoolSentinel(esimCfg.profileId).isPool) {
        profileId = esimCfg.profileId;
      }
      if (!next.apn) next.apn = (run.result?.outputs as any)?.apn;
      // Older executions (and ones still starting) may not carry the model;
      // the modem's own record always does.
      if (!next.modemModel && allocation.deviceId && allocation.modemImei) {
        try {
          const snap = await getDoc(doc(db, 'devices', allocation.deviceId, 'known_modems', allocation.modemImei));
          next.modemModel = snap.data()?.model || undefined;
        } catch {
          // Not visible to this user — the IMEI alone still shows.
        }
      }

      if (profileId) {
        const profile = await getProfile(profileId, run.organizationId).catch(() => null);
        next.esimName = profile ? esimLabel(profile.name, (profile as any).iccid) : profileId;
        next.esimNetwork = profile?.networkName || undefined;
        next.esimGroup = profile?.groupName || undefined;
      }

      if (esimCfg?.mode === 'batch' && esimCfg.batch?.groupId) {
        const group = await getGroup(esimCfg.batch.groupId, run.organizationId).catch(() => null);
        next.selectedGroup = group?.name || esimCfg.batch.groupId;
      } else if (esimCfg?.mode === 'single' && parseSharedPoolSentinel(esimCfg.profileId).isPool) {
        next.selectedGroup = 'Shared pool';
      }

      // Runs from before the option text was stored: the operator name from
      // the allocated modem's scan (what the picker listed), else the catalog.
      if (forcedNetwork && !config.forcedNetworkLabel) {
        if (allocation.deviceId && allocation.modemImei) {
          try {
            const snap = await getDoc(doc(db, 'devices', allocation.deviceId, 'known_modems', allocation.modemImei));
            const scanned: any[] = snap.data()?.last_known?.available_networks || [];
            next.networkName = scanned.find((n) => `${n.mcc}${n.mnc}` === forcedNetwork)?.operator_name || undefined;
          } catch {
            // Not visible to this user.
          }
        }
        if (!next.networkName) {
          // The global catalog entry by MCC/MNC (never another org's custom
          // network). Equality only, so no composite index.
          try {
            const snap = await getDocs(query(collection(db, 'networks'),
              where('isGlobal', '==', true),
              where('mcc', '==', forcedNetwork.slice(0, 3)), where('mnc', '==', forcedNetwork.slice(3)), limit(1)));
            next.networkName = snap.docs[0]?.data()?.name || undefined;
          } catch {
            // Not readable: MCC/MNC alone still shows.
          }
        }
      }

      if (!cancelled) setResolved(next);
    })();
    return () => {
      cancelled = true;
    };
    // run.status: re-read when a run opened mid-flight finishes, so fields the
    // execution writes after start (model, eSIM, APN) appear.
  }, [run.id, run.executionId, run.status, run.organizationId, allocation.deviceId, allocation.modemImei, esimCfg?.mode, esimCfg?.profileId, esimCfg?.batch?.groupId, forcedNetwork]);

  const muted = 'text-muted-foreground';

  const esimValue = (
    <div className="min-w-0">
      <div className="break-words text-foreground">
        {resolved.selectedGroup && (
          <span className={muted}>
            Group: {resolved.selectedGroup}
            {resolved.esimName ? ' → ' : ''}
          </span>
        )}
        {resolved.esimName || (!resolved.selectedGroup && <span className={muted}>—</span>)}
      </div>
      {resolved.esimName && (
        <div className={muted}>
          Network {resolved.esimNetwork || '—'} · {resolved.esimGroup || 'No group'}
        </div>
      )}
    </div>
  );

  const apnValue = apnOverride ? (
    <span className="break-all text-foreground">{apnOverride}</span>
  ) : legacyQuickRun ? (
    <span className="break-all text-foreground">{resolved.apn || <span className={muted}>—</span>}</span>
  ) : (
    <span className="break-all">
      {resolved.apn && <span className="text-foreground">{resolved.apn} </span>}
      <span className={muted}>{resolved.apn ? '(from eSIM)' : 'From eSIM'}</span>
    </span>
  );

  const ratValue = forcedRat && forcedRat !== 'auto' ? (
    <span className="text-foreground">Forced {FORCED_RAT_LABELS[forcedRat] || forcedRat}</span>
  ) : (
    <span className={muted}>{legacyQuickRun ? '—' : 'Auto'}</span>
  );

  const networkValue = forcedNetwork ? (
    <span className="text-foreground">
      {config.forcedNetworkLabel
        || (resolved.networkName ? `${resolved.networkName} (${formatMccMnc(forcedNetwork)})` : formatMccMnc(forcedNetwork))}
    </span>
  ) : (
    <span className={muted}>{legacyQuickRun ? '—' : 'Auto'}</span>
  );

  const modemValue = allocation.modemImei ? (
    <span className="break-all text-foreground">
      {allocation.modemImei}
      {resolved.modemModel && <span className={muted}> ({resolved.modemModel})</span>}
    </span>
  ) : (
    <span className={muted}>Not allocated yet</span>
  );

  const rows: Array<[string, ReactNode]> = [
    ['Location', locationName ? <span className="text-foreground">{locationName}</span> : <span className={muted}>—</span>],
    ['eSIM', esimValue],
    ['APN', apnValue],
    ['Network type', ratValue],
    ['Network', networkValue],
    ['Modem', modemValue],
  ];

  return (
    <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 pt-1 text-xs sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-y-1">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className={`${muted} mt-1 sm:mt-0`}>{label}</dt>
          <dd className="min-w-0">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
